/* eslint-disable no-unused-vars -- parameter names document the vendor queue contract */
import { browser } from '$app/environment';
import type { PixelLoadState } from '$lib/analytics/tiktok-pixel';

type MetaQueue = ((...args: unknown[]) => void) & {
  queue: unknown[][];
  loaded: boolean;
  version: string;
  push: MetaQueue;
  callMethod?: (...args: unknown[]) => void;
};

export interface MetaPixel {
  init(): Promise<boolean>;
  pageView(): boolean;
  track(event: string, properties?: Record<string, unknown>): boolean;
  getState(): PixelLoadState;
}

const SCRIPT_ID = 'quilty-meta-pixel';
const SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';

export function createMetaPixel(pixelId: string): MetaPixel {
  let state: PixelLoadState = pixelId.trim() ? 'idle' : 'disabled';
  let initPromise: Promise<boolean> | null = null;
  let initQueued = false;

  function init(): Promise<boolean> {
    if (!browser || state === 'disabled') return Promise.resolve(false);
    if (state === 'ready') return Promise.resolve(true);
    if (initPromise) return initPromise;

    if (!initQueued) {
      getOrCreateQueue()('init', pixelId);
      initQueued = true;
    }
    state = 'loading';
    initPromise = loadScript().then((loaded) => {
      state = loaded ? 'ready' : 'error';
      if (!loaded) initPromise = null;
      return loaded;
    });
    return initPromise;
  }

  return {
    init,
    pageView: () => callMeta('track', 'PageView'),
    track: (event, properties = {}) => callMeta('track', event, properties),
    getState: () => state,
  };
}

function getOrCreateQueue(): MetaQueue {
  const existing = (window as unknown as { fbq?: MetaQueue }).fbq;
  if (existing) return existing;
  const queue = ((...args: unknown[]) => {
    if (queue.callMethod) {
      queue.callMethod(...args);
    } else {
      queue.queue.push(args);
    }
  }) as MetaQueue;
  queue.queue = [];
  queue.loaded = false;
  queue.version = '2.0';
  queue.push = queue;
  (window as unknown as { fbq: MetaQueue; _fbq: MetaQueue }).fbq = queue;
  (window as unknown as { _fbq: MetaQueue })._fbq = queue;
  return queue;
}

function callMeta(...args: unknown[]): boolean {
  if (!browser) return false;
  const fbq = (window as unknown as { fbq?: MetaQueue }).fbq;
  if (typeof fbq !== 'function') return false;
  try {
    fbq(...args);
    return true;
  } catch {
    return false;
  }
}

function loadScript(): Promise<boolean> {
  return new Promise((resolve) => {
    let existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing?.dataset.quiltyPixelState === 'ready' || isMetaLoaded()) {
      if (existing) existing.dataset.quiltyPixelState = 'ready';
      resolve(true);
      return;
    }
    if (existing?.dataset.quiltyPixelState === 'error') {
      if (existing.dataset.quiltyPixelOwned === 'true') existing.remove();
      else {
        resolve(false);
        return;
      }
      existing = null;
    }
    const element = existing ?? document.createElement('script');
    element.addEventListener('load', () => {
      element.dataset.quiltyPixelState = 'ready';
      resolve(true);
    }, { once: true });
    element.addEventListener('error', () => {
      element.dataset.quiltyPixelState = 'error';
      resolve(false);
    }, { once: true });
    if (!existing) {
      element.id = SCRIPT_ID;
      element.async = true;
      element.src = SCRIPT_URL;
      element.dataset.quiltyPixelOwned = 'true';
      element.dataset.quiltyPixelState = 'loading';
      document.head.appendChild(element);
    }
  });
}

function isMetaLoaded(): boolean {
  const fbq = (window as unknown as { fbq?: MetaQueue }).fbq;
  return fbq?.loaded === true || typeof fbq?.callMethod === 'function';
}

// Backward-compatible exports now route through the consent-aware manager.
export function metaPixelLead(extra?: Record<string, unknown>): void {
  void import('$lib/analytics/ad-tracking').then(({ trackAdLead }) => trackAdLead(extra));
}

export function metaPixelCompleteRegistration(extra?: Record<string, unknown>): void {
  void import('$lib/analytics/ad-tracking').then(({ trackAdRegistration }) =>
    trackAdRegistration(extra),
  );
}

export function metaPixelPurchase(valueUSD: number, sessionId: string): void {
  void import('$lib/analytics/ad-tracking').then(({ trackAdPurchase }) =>
    trackAdPurchase({
      value: valueUSD,
      currency: 'USD',
      sessionId,
      contentId: sessionId,
      quantity: 1,
    }),
  );
}
